import React, { createContext, useContext, useEffect, useState } from 'react';
import { Alert } from 'antd';
import { Link } from 'react-router-dom';
import { TandemStatus, tandemRequest } from './api';

const Context = createContext<TandemStatus | null>(null);
export const useTandem = () => useContext(Context);
export const useTandemTradingBlocked = () => {
  const tandem = useTandem();
  return !!tandem && !tandem.trading_open;
};
export default function TandemSessionBoundary({
  sessionId,
  initial,
  children,
}: {
  sessionId: number;
  initial: TandemStatus;
  children: React.ReactNode;
}) {
  const [status, setStatus] = useState(initial);
  const [stale, setStale] = useState(false);
  const complete = status.state === 'complete';
  useEffect(() => {
    if (complete) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const result = await tandemRequest<{ tandem: TandemStatus }>(
          `/tandem-session-status/${sessionId}/`,
          'GET',
          undefined,
          controller.signal,
        );
        if (!stopped) {
          if (result.tandem) {
            setStatus(result.tandem);
            setStale(false);
            if (result.tandem.state === 'complete') return;
          } else setStale(true);
        }
      } catch {
        if (!stopped) setStale(true);
      }
      if (!stopped) timer = setTimeout(() => void poll(), 1000);
    };
    void poll();
    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [sessionId, complete]);
  const value = { ...status, trading_open: !stale && status.trading_open };
  return (
    <Context.Provider value={value}>
      <Alert
        banner
        type={status.state === 'inspection' || stale ? 'warning' : 'info'}
        message={
          <>
            Tandem {status.pair_id.slice(0, 8)} · {status.side} engine.{' '}
            {stale
              ? 'Connection unconfirmed; trading is temporarily unavailable.'
              : value.trading_open
                ? 'Both engines ready. Place trades independently in each session.'
                : status.state === 'complete'
                  ? 'Comparison complete — fast session ended.'
                  : 'Trading is unavailable until both engines confirm completion.'}{' '}
            Session settings and advances are managed by the tandem controller.
            {status.can_control && (
              <>
                {' '}
                <Link to={status.control_url}>Open tandem</Link>
              </>
            )}
          </>
        }
      />
      {children}
    </Context.Provider>
  );
}
